
// pages/GroupsPage.tsx
import React, { useState, useMemo, useContext, memo, useRef } from 'react';
import { isUserOnline } from '../utils';
import { useAuth } from '../AuthContext';
// FIX: Changed import to correct PageContext file to avoid circular dependency
import { PageContext } from '../PageContext';
import { GroupGame, Page, UserProfile, defaultImageEdits, getSafeImageEdits } from '../types';
import { mockRecommendedGroups } from '../mockData';
import CreateGameModal from '../components/CreateGameModal';
import ConfirmationModal from '../components/ConfirmationModal';

const GroupCard: React.FC<{
    group: GroupGame;
    onDelete: (groupId: string) => void;
    onInvite: (groupId: string) => void;
    isCreator: boolean;
    onClick: () => void;
}> = memo(({ group, onDelete, onInvite, isCreator, onClick }) => {
    const { allUsers } = useAuth();
    const members = useMemo(() => allUsers.filter(u => group.members.includes(u.uid)), [group.members, allUsers]);
    
    const handleDelete = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDelete(group.id);
    };

    const handleInvite = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onInvite(group.id);
    };

    const imageEdits = getSafeImageEdits(group.imageEdits);
    const imageStyle: React.CSSProperties = {
        filter: `brightness(${imageEdits.adjustments.brightness}%) contrast(${imageEdits.adjustments.contrast}%) saturate(${imageEdits.adjustments.saturate}%) blur(${imageEdits.adjustments.blur}px)`,
        transform: `scale(${imageEdits.adjustments.zoom}) translate(${imageEdits.adjustments.offsetX}px, ${imageEdits.adjustments.offsetY}px)`,
    };

    return (
        <div 
            onClick={onClick} 
            className="relative aspect-[3/4] w-full bg-gray-200 rounded-xl shadow-lg cursor-pointer overflow-hidden group transform transition-transform hover:scale-105"
        >
            {/* Background Image */}
            <div className={`absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-110 overflow-hidden`}>
                 <div className={`frame-container ${imageEdits.frame} w-full h-full`}>
                    <img 
                        src={group.imageUrl || 'https://images.unsplash.com/photo-1528459801416-a9e53bbf4e17?q=80&w=1912&auto=format&fit=crop'} 
                        alt={group.name} 
                        className={`w-full h-full object-cover ${imageEdits.filter}`} 
                        style={imageStyle}
                    />
                </div>
            </div>
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
            
            {/* Action Buttons (Top Right) */}
            <div className="absolute top-2 right-2 z-50 flex flex-col gap-2">
                {/* Invite Button */}
                <button 
                    onClick={handleInvite} 
                    onMouseDown={(e) => e.stopPropagation()}
                    className="p-2 rounded-full bg-black/40 text-white hover:bg-[var(--accent-pink)] transition-colors backdrop-blur-md shadow-md border border-white/10"
                    aria-label="Invite friends"
                >
                    <i className="ph-duotone ph-user-plus text-lg"></i>
                </button>

                {/* Delete Button (Creator Only) */}
                {isCreator && (
                    <button 
                        onClick={handleDelete} 
                        onMouseDown={(e) => e.stopPropagation()}
                        className="p-2 rounded-full bg-black/40 text-white hover:bg-red-500 transition-colors backdrop-blur-md shadow-md border border-white/10"
                        aria-label="Delete group"
                    >
                        <i className="ph-duotone ph-trash text-lg"></i>
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                <h3 className="font-bold text-lg truncate" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>{group.name}</h3>
                <div className="flex items-center -space-x-2 mt-1">
                    {members.slice(0, 3).map(m => (
                        <div key={m.uid} className="relative">
                            <img 
                                src={m.photoURL} 
                                alt={m.displayName} 
                                className="w-6 h-6 rounded-full object-cover border-2 border-white/50" 
                                title={m.displayName}
                            />
                            {isUserOnline(m) && (
                                <span className="absolute bottom-0 right-0 block h-2 w-2 rounded-full bg-green-500 border border-white/50" title="Online"></span>
                            )}
                        </div>
                    ))}
                    {members.length > 3 && (
                        <div className="w-6 h-6 rounded-full bg-black/50 flex items-center justify-center text-xs font-bold border-2 border-white/50">
                            +{members.length-3}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});


const GroupsPage: React.FC = () => {
    const { userProfile, createGroup, joinGame, deleteGroup, groups } = useAuth();
    const pageContext = useContext(PageContext);
    
    // UI State
    const [isCreateGameModalOpen, setCreateGameModalOpen] = useState(false);
    const [createModalTriggerRect, setCreateModalTriggerRect] = useState<DOMRect | null>(null);
    const [showJoinInput, setShowJoinInput] = useState(false);
    const [joinCode, setJoinCode] = useState('');
    
    // Confirmation Modal State
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
    
    const discoverContainerRef = useRef<HTMLDivElement>(null);
    const createGroupBannerRef = useRef<HTMLDivElement>(null);
    const touchStartX = useRef(0);

    const userGroups = useMemo(() => {
        if (!userProfile) return [];
        return groups.filter(g => g.members.includes(userProfile.uid));
    }, [userProfile, groups]);

    const groupsWithAds = useMemo(() => {
        return userGroups;
    }, [userGroups]);
    
    const recommendedGroups = mockRecommendedGroups;

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.targetTouches[0].clientX;
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!discoverContainerRef.current) return;

        const touchEndX = e.changedTouches[0].clientX;
        const swipeDistance = touchStartX.current - touchEndX;
        const minSwipeDistance = 50;

        const scrollAmount = 160 + 16;

        if (Math.abs(swipeDistance) > minSwipeDistance) {
            if (swipeDistance > 0) { // Swipe Left
                discoverContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            } else { // Swipe Right
                discoverContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            }
        }
    };

    const handleJoinSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (joinCode) joinGame(joinCode);
        setJoinCode('');
        setShowJoinInput(false);
    };

    const handleDeleteClick = (groupId: string) => {
        setGroupToDelete(groupId);
        setConfirmModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (groupToDelete) {
            try {
                await deleteGroup(groupToDelete);
                setConfirmModalOpen(false);
                setGroupToDelete(null);
            } catch (error) {
                console.error("Delete group failed:", error);
                alert('Failed to delete group. Please try again.');
                setConfirmModalOpen(false); // Close on error too
            }
        }
    };
    
    const handleCreateGroup = async (groupData: Omit<GroupGame, 'id' | 'creatorId' | 'members' | 'admins' | 'scores' | 'solvedPuzzles' | 'joinRequests' | 'inviteCode'>) => {
        const newId = await createGroup(groupData);
        if (newId) {
            // Redirect to Invite Page instead of showing modal
            pageContext?.switchPage(Page.InviteFriends, 'Invite Friends', { groupId: newId });
        }
    };

    const handleGroupClick = (group: GroupGame) => {
        pageContext?.switchPage(Page.GameDetail, group.name, { gameId: group.id });
    };
    
    const handleInviteClick = (groupId: string) => {
        pageContext?.switchPage(Page.InviteFriends, 'Invite Friends', { groupId });
    };

    const openCreateModal = () => {
        if (createGroupBannerRef.current) {
            setCreateModalTriggerRect(createGroupBannerRef.current.getBoundingClientRect());
        }
        setCreateGameModalOpen(true);
    };

    if (!userProfile) {
        return <p className="text-center text-gray-500">Please log in to see your groups.</p>;
    }

    return (
        <>
            <div className="space-y-4">
                <h2 className="text-2xl font-bold">
                    <span className="aurora-text">Groups</span>
                </h2>
                
                {/* Create a Group Banner */}
                <div
                    ref={createGroupBannerRef}
                    onClick={openCreateModal}
                    className="bg-[var(--primary-dark-purple)] p-3 rounded-lg shadow-md cursor-pointer hover:bg-purple-900 transition-colors"
                >
                    <div className="flex items-center space-x-3">
                        <div className="flex-grow bg-white rounded-lg px-4 py-2 text-center flex items-center justify-center min-h-[40px]">
                            <p
                                className="text-3xl font.bold aurora-text"
                                style={{ fontFamily: "'Caveat', cursive" }}
                            >
                                Start a new community!
                            </p>
                        </div>
                        <i className="ph-duotone ph-users-three text-2xl text-[var(--accent-pink)]"></i>
                    </div>
                </div>

                {/* Join Group Section */}
                <div className="text-center">
                    <button 
                        onClick={() => setShowJoinInput(!showJoinInput)} 
                        className="font-bold text-lg"
                    >
                        {showJoinInput ? (
                             <span className="text-gray-600 hover:text-[var(--primary-dark-purple)]">Cancel</span>
                        ) : (
                            <span className="aurora-text">Have an invite code? Join a group</span>
                        )}
                    </button>
                </div>

                {showJoinInput && (
                    <form onSubmit={handleJoinSubmit} className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg transition-all duration-300">
                        <input type="text" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="Enter invite code..." className="flex-1 border-gray-300 rounded-lg shadow-sm" />
                        <button type="submit" className="aurora-text font-bold text-lg px-4 transition-transform hover:scale-110">Join</button>
                    </form>
                )}

                {/* Discover Groups Section */}
                <div className="pt-4">
                    <h2 className="text-xl font-bold text-gray-800 mb-2 px-1">Discover Groups</h2>
                    <div 
                        ref={discoverContainerRef}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        className="flex space-x-4 overflow-x-auto pb-2 -mx-4 px-4 custom-scrollbar"
                    >
                        {recommendedGroups.map((group, index) => (
                            <React.Fragment key={group.id}>
                                <div className="w-40 flex-shrink-0">
                                    <GroupCard 
                                        group={group}
                                        onClick={() => handleGroupClick(group)}
                                        onDelete={() => {}} // Cannot delete recommended groups
                                        onInvite={() => {}} // Cannot invite to recommended groups from here usually
                                        isCreator={false} // Cannot be creator of a group you're not in
                                    />
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className="pt-2">
                    <h2 className="text-xl font-bold text-gray-800 mb-2 px-1">My Groups</h2>
                    {userGroups.length > 0 ? (
                        <div className="grid grid-cols-2 gap-4">
                            {groupsWithAds.map(item => {
                                return (
                                    <GroupCard 
                                        key={item.id} 
                                        group={item as GroupGame}
                                        onClick={() => handleGroupClick(item as GroupGame)}
                                        onDelete={handleDeleteClick}
                                        onInvite={handleInviteClick}
                                        isCreator={(item as GroupGame).creatorId === userProfile.uid}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-center text-gray-400 pt-8">You haven't joined any groups yet.</p>
                    )}
                </div>
            </div>
            
            <CreateGameModal isOpen={isCreateGameModalOpen} onClose={() => setCreateGameModalOpen(false)} onCreate={handleCreateGroup} triggerRect={createModalTriggerRect}/>
            
            <ConfirmationModal 
                isOpen={isConfirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Group?"
                message="Are you sure you want to delete this group? This action cannot be undone and all data will be lost."
                confirmText="Delete Group"
                triggerRect={null}
            />
        </>
    );
};

export default GroupsPage;
